import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminSettings, Customer, CustomerCategory, Quote, ZohoEstimateSummary, ZohoInvoiceSummary } from '../types';
import { customerApi } from '../services/customerApi';
import {
  zohoCRMService,
  ZohoContact,
  ZohoInitialImportSamples,
  ZohoInitialImportSummary,
  ZohoPreviewSummary,
  ZohoSyncApprovalPlan,
} from '../services/zohoCRM';
import { customerCategoriesService } from '../services/customerCategories';
import CustomerToolbar from './customers/CustomerToolbar';
import ZohoImportModal from './customers/ZohoImportModal';
import ZohoInitialImportModal from './customers/ZohoInitialImportModal';
import CustomerAddForm from './customers/CustomerAddForm';
import CommercialTermsModal from './customers/CommercialTermsModal';
import CustomerCard from './customers/CustomerCard';
import { CustomerSourceFilter, CustomerStatusFilter } from '../hooks/useCustomerFilters';
import { useZohoFinanceCache } from '../hooks/useZohoFinanceCache';
import { resolveZohoCurrency } from '../utils/resolveZohoCurrency';

interface CustomerManagerProps {
  customers: Customer[];
  setCustomers: React.Dispatch<React.SetStateAction<Customer[]>>;
  quotes: Quote[];
  settings: AdminSettings;
  isSyncing: boolean;
}

const PAGE_SIZE = 50;

const CustomerManager: React.FC<CustomerManagerProps> = ({
  customers,
  setCustomers,
  quotes: _quotes,
  settings,
  isSyncing,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [showZohoModal, setShowZohoModal] = useState(false);
  const [zohoModalMode, setZohoModalMode] = useState<'import' | 'link'>('import');
  const [zohoContacts, setZohoContacts] = useState<ZohoContact[]>([]);
  const [isLoadingZoho, setIsLoadingZoho] = useState(false);
  const [isSearchingZoho, setIsSearchingZoho] = useState(false);
  const [zohoSearchQuery, setZohoSearchQuery] = useState('');
  const [zohoPreviewSummary, setZohoPreviewSummary] = useState<ZohoPreviewSummary | null>(null);
  const [showZohoInitialImportModal, setShowZohoInitialImportModal] = useState(false);
  const [zohoInitialImportSummary, setZohoInitialImportSummary] = useState<ZohoInitialImportSummary | null>(null);
  const [zohoInitialImportSamples, setZohoInitialImportSamples] = useState<ZohoInitialImportSamples | null>(null);
  const [zohoInitialImportUnavailable, setZohoInitialImportUnavailable] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [categories, setCategories] = useState<CustomerCategory[]>([]);
  const [onboardingContactId, setOnboardingContactId] = useState<string | null>(null);
  const [linkingContactId, setLinkingContactId] = useState<string | null>(null);
  const [linkTargetCustomer, setLinkTargetCustomer] = useState<Customer | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isSavingTerms, setIsSavingTerms] = useState(false);
  const [isRefreshingAllZoho, setIsRefreshingAllZoho] = useState(false);
  const [editingQuotes, setEditingQuotes] = useState<Quote[]>([]);
  const [zohoFinance, setZohoFinance] = useState<{ invoices: ZohoInvoiceSummary; estimates: ZohoEstimateSummary } | null>(null);
  const [expandedCustomerId, setExpandedCustomerId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState<CustomerSourceFilter>('ALL');
  const [filterStatus, setFilterStatus] = useState<CustomerStatusFilter>('ALL');
  const [filterOutstandingOnly, setFilterOutstandingOnly] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPageSize] = useState(PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [pagedCustomers, setPagedCustomers] = useState<Customer[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  const [editTerms, setEditTerms] = useState({
    charterDiscount: 0,
    scheduleDiscount: 0,
    creditLimit: 0,
    paymentPolicyType: 'UNKNOWN' as NonNullable<Customer['paymentPolicyType']>,
    paymentPolicyLabel: '',
    creditStatus: 'UNKNOWN' as NonNullable<Customer['creditStatus']>,
    requiresPrepayment: false,
    bookingBlockMode: 'NONE' as NonNullable<Customer['bookingBlockMode']>,
    paymentPolicyNotes: '',
  });
  const [editCategoryId, setEditCategoryId] = useState<string>('');
  const [applyCategoryDefaults, setApplyCategoryDefaults] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    legalName: '',
    tin: '',
    email: '',
    phoneNumber: '',
    company: '',
    charterDiscount: 0,
    scheduleDiscount: 0,
    creditLimit: 10000,
    categoryId: '',
    createInZoho: true,
  });

  const { zohoFinanceByCustomer, loadingFinanceId, loadFinance } = useZohoFinanceCache();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    void loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await customerCategoriesService.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadCustomerPage = useCallback(
    async (pageOverride?: number) => {
      const nextPage = pageOverride || customerPage;
      setIsLoadingCustomers(true);
      try {
        const result = await customerApi.listPaged({
          page: nextPage,
          pageSize: customerPageSize,
          search: debouncedSearchTerm || undefined,
          source: filterSource,
          status: filterStatus,
          outstandingOnly: filterOutstandingOnly,
        });
        setPagedCustomers(result.items);
        setTotalCount(result.totalCount);
        setCustomerPage(result.page);
      } catch (error) {
        console.error('Failed to load customers page:', error);
        setPagedCustomers([]);
        setTotalCount(0);
      } finally {
        setIsLoadingCustomers(false);
      }
    },
    [customerPage, customerPageSize, debouncedSearchTerm, filterSource, filterOutstandingOnly, filterStatus]
  );

  useEffect(() => {
    void loadCustomerPage();
  }, [loadCustomerPage]);

  useEffect(() => {
    setCustomerPage(1);
  }, [debouncedSearchTerm, filterSource, filterStatus, filterOutstandingOnly]);

  const refreshCurrentPage = useCallback(
    async (pageOverride?: number) => {
      await loadCustomerPage(pageOverride);
    },
    [loadCustomerPage]
  );

  const mergeGlobalCustomer = useCallback(
    (updated: Customer) => {
      setCustomers((prev) => {
        const existingIndex = prev.findIndex((customer) => customer.id === updated.id);
        if (existingIndex === -1) return prev;
        return prev.map((customer) => (customer.id === updated.id ? { ...customer, ...updated } : customer));
      });
    },
    [setCustomers]
  );

  const replaceLoadedCustomer = useCallback((updated: Customer) => {
    setPagedCustomers((prev) => prev.map((customer) => (customer.id === updated.id ? { ...customer, ...updated } : customer)));
  }, []);

  const handleZohoImport = async () => {
    setShowZohoModal(true);
    setZohoModalMode('import');
    setLinkTargetCustomer(null);
    setIsLoadingZoho(true);
    setZohoSearchQuery('');
    try {
      const result = await zohoCRMService.previewContacts('BOOKS');
      setZohoContacts(result.contacts);
      setZohoPreviewSummary(result.summary);
    } catch (error) {
      console.error('Failed to preview Zoho contacts:', error);
      setZohoContacts([]);
      setZohoPreviewSummary(null);
    } finally {
      setIsLoadingZoho(false);
    }
  };

  const handleZohoInitialImport = async () => {
    if (zohoInitialImportUnavailable) {
      alert('Initial Import All Zoho Clients is not available yet on the backend serving this session. Please deploy/restart the backend and try again.');
      return;
    }
    setShowZohoInitialImportModal(true);
    setIsLoadingZoho(true);
    try {
      const result = await zohoCRMService.previewInitialImport('BOOKS');
      setZohoInitialImportSummary(result.summary);
      setZohoInitialImportSamples(result.samples);
    } catch (error) {
      const status = (error as any)?.response?.status;
      const details = (error as any)?.response?.data?.details;
      const message = (error as any)?.response?.data?.error;
      const fallbackMessage = (error as any)?.message;
      if (status === 404) {
        setZohoInitialImportUnavailable(true);
        setShowZohoInitialImportModal(false);
        setZohoInitialImportSummary(null);
        setZohoInitialImportSamples(null);
        alert('Initial Import All Zoho Clients is not available on the current backend yet. Please deploy/restart the backend and try again.');
        return;
      }
      console.error('Failed to preview initial Zoho import:', error);
      setZohoInitialImportSummary(null);
      setZohoInitialImportSamples(null);
      alert(message || details || fallbackMessage || 'Failed to preview initial Zoho import');
    } finally {
      setIsLoadingZoho(false);
    }
  };

  useEffect(() => {
    if (!showZohoModal || !zohoSearchQuery || zohoSearchQuery.trim().length < 2) {
      if (showZohoModal && zohoModalMode === 'import') {
        void zohoCRMService.previewContacts('BOOKS')
          .then((result) => {
            setZohoContacts(result.contacts);
            setZohoPreviewSummary(result.summary);
          })
          .catch(() => {
            setZohoContacts([]);
            setZohoPreviewSummary(null);
          });
      } else {
        setZohoContacts([]);
        setZohoPreviewSummary(null);
      }
      return;
    }

    const delayDebounce = window.setTimeout(async () => {
      setIsSearchingZoho(true);
      try {
        const result = await zohoCRMService.searchContacts(zohoSearchQuery);
        setZohoContacts(result.contacts);
        setZohoPreviewSummary(result.summary);
      } catch (error: any) {
        console.error('Failed to search Zoho contacts:', error);
        const errorData = error.response?.data;
        let errorMessage = 'Failed to search contacts';

        if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
          errorMessage = 'Search timed out. Please try again with a more specific search term.';
        } else if (error.message?.includes('Network Error') || error.message?.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }

        alert(`⚠️ ${errorMessage}`);
        setZohoContacts([]);
        setZohoPreviewSummary(null);
      } finally {
        setIsSearchingZoho(false);
      }
    }, 500);

    return () => window.clearTimeout(delayDebounce);
  }, [showZohoModal, zohoModalMode, zohoSearchQuery]);

  const handleOnboardContact = async (contact: ZohoContact) => {
    if (onboardingContactId) return;

    setOnboardingContactId(contact.contactId);
    try {
      await zohoCRMService.onboardContact(contact.contactId, selectedCategoryId || undefined);
      await refreshCurrentPage(1);

      setZohoContacts((prev) => prev.filter((candidate) => candidate.contactId !== contact.contactId));
      setZohoPreviewSummary((prev) =>
        prev
          ? {
              ...prev,
              newCount: Math.max(0, prev.newCount - 1),
            }
          : prev
      );

      alert(`✅ ${contact.contactName} onboarded successfully${selectedCategoryId ? ' with discount group' : ''}!`);
    } catch (error: any) {
      console.error('Failed to onboard contact:', error);
      const errorData = error.response?.data;
      if (errorData?.error === 'Customer already exists') {
        alert(`⚠️ ${contact.contactName} is already in your customer list.`);
      } else {
        alert(errorData?.error || errorData?.message || 'Failed to onboard contact');
      }
    } finally {
      setOnboardingContactId(null);
    }
  };

  const openZohoLink = useCallback((customer: Customer) => {
    setLinkTargetCustomer(customer);
    setZohoModalMode('link');
    setShowZohoModal(true);
    setZohoContacts([]);
    setZohoSearchQuery('');
  }, []);

  const handleLinkContact = async (contact: ZohoContact) => {
    if (!linkTargetCustomer || linkingContactId) return;
    setLinkingContactId(contact.contactId);
    try {
      const updated = await customerApi.update(linkTargetCustomer.id, {
        zohoContactId: contact.contactId,
      });
      mergeGlobalCustomer(updated);
      replaceLoadedCustomer(updated);
      alert(`✅ Linked ${linkTargetCustomer.name} to ${contact.contactName}`);
      setLinkTargetCustomer(null);
      setZohoModalMode('import');
      setShowZohoModal(false);
    } catch (error: any) {
      console.error('Failed to link Zoho contact:', error);
      const errData = error.response?.data;
      alert(errData?.error || errData?.message || 'Failed to link Zoho contact');
    } finally {
      setLinkingContactId(null);
    }
  };

  const handleZohoSync = async (approvalPlan?: ZohoSyncApprovalPlan, categoryId?: string) => {
    setIsLoadingZoho(true);
    try {
      const result = await zohoCRMService.syncContacts({
        updateExisting: true,
        overwriteLocalFields: false,
        selectedNewContactIds: approvalPlan?.selectedNewContactIds || [],
        selectedLinkedContactIds: approvalPlan?.selectedLinkedContactIds || [],
        selectedReviewContactIds: approvalPlan?.selectedReviewContactIds || [],
        skippedContactIds: approvalPlan?.skippedContactIds || [],
        categoryId: categoryId || selectedCategoryId || undefined,
      });
      await refreshCurrentPage();
      alert(
        `Zoho pull complete!\nImported: ${result.imported}\nLinked: ${result.linked || 0}\nFinance Refreshed: ${result.updated}\nReview: ${result.review || 0}\nDuplicates: ${result.duplicate || 0}\nSkipped: ${result.skipped}`
      );
      setShowZohoModal(false);
    } catch (error: any) {
      console.error('Failed to sync Zoho contacts:', error);
      alert(error.response?.data?.error || 'Failed to sync contacts');
    } finally {
      setIsLoadingZoho(false);
    }
  };

  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const customerData: any = {
        name: formData.name,
        legalName: formData.legalName || undefined,
        tin: formData.tin || undefined,
        email: formData.email || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        company: formData.company || undefined,
        charterDiscount: formData.charterDiscount,
        scheduleDiscount: formData.scheduleDiscount,
        creditLimit: formData.creditLimit,
        status: 'ACTIVE' as const,
        createInZoho: formData.createInZoho,
      };
      if (!formData.createInZoho) {
        customerData.source = 'LOCAL' as const;
      }

      const newCustomer = await customerApi.create(customerData);

      if (formData.categoryId) {
        await customerCategoriesService.bulkAssign(formData.categoryId, [newCustomer.id], true);
      }

      setCustomers((prev) => [newCustomer, ...prev]);
      await refreshCurrentPage(1);
      setIsAdding(false);
      setFormData({
        name: '',
        legalName: '',
        tin: '',
        email: '',
        phoneNumber: '',
        company: '',
        charterDiscount: 0,
        scheduleDiscount: 0,
        creditLimit: 10000,
        categoryId: '',
        createInZoho: true,
      });
    } catch (error: any) {
      console.error('Failed to create customer:', error);
      const errData = error.response?.data;
      const message = errData?.details || errData?.error || errData?.message || 'Failed to create customer';
      alert(message);
    }
  };

  const openTermsEditor = useCallback((customer: Customer) => {
    const currentCategoryId = customer.categoryAssignments?.[0]?.Category?.id || '';
    setEditingCustomer(customer);
    setEditTerms({
      charterDiscount: customer.charterDiscount || 0,
      scheduleDiscount: customer.scheduleDiscount || 0,
      creditLimit: customer.creditLimit || 0,
      paymentPolicyType: customer.paymentPolicyType || 'UNKNOWN',
      paymentPolicyLabel: customer.paymentPolicyLabel || '',
      creditStatus: customer.creditStatus || 'UNKNOWN',
      requiresPrepayment: !!customer.requiresPrepayment,
      bookingBlockMode: customer.bookingBlockMode || 'NONE',
      paymentPolicyNotes: customer.paymentPolicyNotes || '',
    });
    setEditCategoryId(currentCategoryId);
    setApplyCategoryDefaults(false);
    setEditingQuotes([]);
    setZohoFinance(null);
    customerApi
      .getById(customer.id)
      .then((detail: any) => {
        if (detail) {
          setEditingCustomer(detail);
          const quotesList = detail.Quote || detail.quotes || [];
          setEditingQuotes(Array.isArray(quotesList) ? quotesList : []);
          if (detail.categoryAssignments?.[0]?.Category?.id) {
            setEditCategoryId(detail.categoryAssignments[0].Category.id);
          }
        }
      })
      .catch(() => {});
  }, []);

  const editCurrency = editingCustomer
    ? resolveZohoCurrency(settings, editingCustomer, zohoFinance || undefined)
    : settings?.defaultCurrency || 'USD';

  const saveTerms = async () => {
    if (!editingCustomer) return;
    setIsSavingTerms(true);
    try {
      let updated = editingCustomer;
      const currentCategoryId = editingCustomer.categoryAssignments?.[0]?.Category?.id || '';
      const categoryChanged = (editCategoryId || '') !== currentCategoryId;

      if (categoryChanged || (!!editCategoryId && applyCategoryDefaults)) {
        updated = await customerApi.setCategory(editingCustomer.id, editCategoryId || null, applyCategoryDefaults);
      }

      if (!applyCategoryDefaults) {
        updated = await customerApi.update(editingCustomer.id, {
          charterDiscount: editTerms.charterDiscount,
          scheduleDiscount: editTerms.scheduleDiscount,
          creditLimit: editTerms.creditLimit,
          paymentPolicyType: editTerms.paymentPolicyType,
          paymentPolicyLabel: editTerms.paymentPolicyLabel || null,
          creditStatus: editTerms.creditStatus,
          requiresPrepayment: editTerms.requiresPrepayment,
          bookingBlockMode: editTerms.bookingBlockMode,
          paymentPolicyNotes: editTerms.paymentPolicyNotes || null,
        });
      }

      mergeGlobalCustomer(updated);
      replaceLoadedCustomer(updated);
      setEditingCustomer(null);
    } catch (error: any) {
      console.error('Failed to update terms:', error);
      alert(error.response?.data?.error || 'Failed to update commercial terms');
    } finally {
      setIsSavingTerms(false);
    }
  };

  const getCustomerCategory = useCallback((customer: Customer): CustomerCategory | null => {
    if (customer.categoryAssignments && customer.categoryAssignments.length > 0) {
      return customer.categoryAssignments[0].Category;
    }
    return null;
  }, []);

  const handleRefreshAllZoho = async () => {
    if (!confirm('Refresh finance data for all Zoho-linked customers?')) return;
    setIsRefreshingAllZoho(true);
    try {
      await customerApi.refreshAllZoho();
      await refreshCurrentPage();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to refresh Zoho balances');
    } finally {
      setIsRefreshingAllZoho(false);
    }
  };

  const handleRemoveClient = useCallback(
    async (customer: Customer) => {
      if (!confirm(`Deactivate ${customer.name}? This will remove the client from active lists.`)) return;
      try {
        await customerApi.delete(customer.id);
        setCustomers((prev) => prev.filter((entry) => entry.id !== customer.id));
        await refreshCurrentPage();
      } catch (error: any) {
        alert(error.response?.data?.error || 'Failed to remove customer');
      }
    },
    [refreshCurrentPage, setCustomers]
  );

  const handleRemoveGroup = useCallback(
    async (customer: Customer) => {
      if (!confirm('Remove discount group from this customer? This will NOT change current discounts.')) return;
      try {
        const updated = await customerApi.setCategory(customer.id, null, false);
        mergeGlobalCustomer(updated);
        replaceLoadedCustomer(updated);
      } catch (error: any) {
        alert(error.response?.data?.error || 'Failed to remove discount group');
      }
    },
    [mergeGlobalCustomer, replaceLoadedCustomer]
  );

  const handleOpenZohoContact = useCallback((zohoContactId: string) => {
    window.open(`https://books.zoho.com/app#/contacts/${zohoContactId}`, '_blank');
  }, []);

  const handleRefreshZohoCustomer = useCallback(
    async (customer: Customer) => {
      try {
        const refreshed = await customerApi.refreshFromZoho(customer.id);
        mergeGlobalCustomer(refreshed);
        replaceLoadedCustomer(refreshed);
      } catch (error: any) {
        alert(error.response?.data?.error || 'Failed to refresh customer from Zoho');
      }
    },
    [mergeGlobalCustomer, replaceLoadedCustomer]
  );

  const handleRefreshFinance = useCallback(async (customer: Customer) => {
    if (!customer.zohoContactId) return;
    try {
      await loadFinance(customer.id);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to refresh Zoho finance');
    }
  }, [loadFinance]);

  const handleToggleExpand = useCallback((customerId: string) => {
    setExpandedCustomerId((prev) => (prev === customerId ? null : customerId));
  }, []);

  const scoredCustomers = useMemo(() => {
    return pagedCustomers.map((customer) => ({
      customer,
      score:
        Number(customer.zohoInvoicesTotal || 0) ||
        Number(customer.zohoInvoicesCount || 0) ||
        Number(customer.outstandingBalance || 0),
    }));
  }, [pagedCustomers]);

  const top10ByTransactions = useMemo(() => {
    return [...scoredCustomers]
      .sort((left, right) => right.score - left.score)
      .slice(0, 10)
      .map((entry) => entry.customer);
  }, [scoredCustomers]);

  const regularCustomers = useMemo(() => {
    const priorityIds = new Set(top10ByTransactions.map((customer) => customer.id));
    return pagedCustomers.filter((customer) => !priorityIds.has(customer.id));
  }, [pagedCustomers, top10ByTransactions]);

  const totalPages = Math.max(1, Math.ceil(totalCount / customerPageSize));
  const pageStart = totalCount === 0 ? 0 : (customerPage - 1) * customerPageSize + 1;
  const pageEnd = totalCount === 0 ? 0 : Math.min(totalCount, customerPage * customerPageSize);

  const renderCustomerCard = (customer: Customer, isPriority?: boolean) => {
    const category = getCustomerCategory(customer);
    const quotesCount = customer.totalQuotesSent ?? customer._count?.Quote ?? 0;
    const quoteValue = customer.totalRevenue ?? 0;
    const lastQuoteDate = customer.lastQuoteDate || null;
    const lastQuoteStatus = customer.lastQuoteStatus || null;
    const lastQuoteTotal = customer.lastQuoteTotal ?? 0;
    const isExpanded = expandedCustomerId === customer.id;
    const finance = zohoFinanceByCustomer[customer.id];
    const cachedFinance =
      customer.zohoInvoicesCount !== undefined || customer.zohoInvoicesTotal !== undefined || customer.zohoInvoicesBalance !== undefined
        ? {
            invoices: {
              count: customer.zohoInvoicesCount || 0,
              total: customer.zohoInvoicesTotal || 0,
              balance: customer.zohoInvoicesBalance || 0,
              last: customer.zohoLastInvoiceDate ? { date: customer.zohoLastInvoiceDate } : undefined,
              currencyCode: customer.zohoCurrencyCode,
            },
            estimates: {
              count: customer.zohoEstimatesCount || 0,
              total: customer.zohoEstimatesTotal || 0,
              last: customer.zohoLastEstimateDate ? { date: customer.zohoLastEstimateDate } : undefined,
              currencyCode: customer.zohoCurrencyCode,
            },
          }
        : null;
    const activeFinance = finance || cachedFinance;
    const financeCurrency = resolveZohoCurrency(settings, customer, activeFinance || undefined);
    const displayOutstanding = activeFinance?.invoices?.balance ?? customer.zohoInvoicesBalance ?? customer.outstandingBalance ?? 0;
    const displayInvoicesTotal = activeFinance?.invoices?.total ?? customer.zohoInvoicesTotal ?? 0;
    const displayInvoicesCount = activeFinance?.invoices?.count ?? customer.zohoInvoicesCount ?? 0;
    const displayEstimatesTotal = activeFinance?.estimates?.total ?? customer.zohoEstimatesTotal ?? 0;
    const displayEstimatesCount = activeFinance?.estimates?.count ?? customer.zohoEstimatesCount ?? 0;
    const hasMaosActivity = quotesCount > 0 || quoteValue > 0;
    const lastInvoice = activeFinance?.invoices?.last;
    const lastEstimate = activeFinance?.estimates?.last;
    const lastActivityDate = lastInvoice?.date || lastEstimate?.date || lastQuoteDate || null;
    const lastActivityLabel = lastInvoice?.date
      ? `Invoice ${lastInvoice.status ? `(${lastInvoice.status})` : ''}`.trim()
      : lastEstimate?.date
        ? `Estimate ${lastEstimate.status ? `(${lastEstimate.status})` : ''}`.trim()
        : lastQuoteDate
          ? `Quote ${lastQuoteStatus ? `(${lastQuoteStatus})` : ''}`.trim()
          : 'No recent activity';

    return (
      <CustomerCard
        key={customer.id}
        customer={customer}
        category={category}
        quotesCount={quotesCount}
        quoteValue={quoteValue}
        lastQuoteDate={lastQuoteDate}
        lastQuoteStatus={lastQuoteStatus}
        lastQuoteTotal={lastQuoteTotal}
        isExpanded={isExpanded}
        onToggleExpand={handleToggleExpand}
        onAdjustTerms={openTermsEditor}
        onLinkZohoContact={openZohoLink}
        onRemoveClient={handleRemoveClient}
        onRemoveGroup={handleRemoveGroup}
        onOpenZohoContact={handleOpenZohoContact}
        onRefreshZohoCustomer={handleRefreshZohoCustomer}
        onRefreshFinance={handleRefreshFinance}
        loadingFinanceId={loadingFinanceId}
        isPriority={isPriority}
        financeCurrency={financeCurrency}
        displayOutstanding={displayOutstanding}
        displayInvoicesTotal={displayInvoicesTotal}
        displayInvoicesCount={displayInvoicesCount}
        displayEstimatesTotal={displayEstimatesTotal}
        displayEstimatesCount={displayEstimatesCount}
        activeFinance={activeFinance}
        lastInvoice={lastInvoice || null}
        lastEstimate={lastEstimate || null}
        lastActivityDate={lastActivityDate}
        lastActivityLabel={lastActivityLabel}
        hasMaosActivity={hasMaosActivity}
        lastZohoEstimate={null}
      />
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-10 animate-in fade-in duration-500 pb-20">
      <CustomerToolbar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterSource={filterSource}
        setFilterSource={setFilterSource}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterOutstandingOnly={filterOutstandingOnly}
        setFilterOutstandingOnly={setFilterOutstandingOnly}
        onRefreshAllZoho={handleRefreshAllZoho}
        isRefreshingAllZoho={isRefreshingAllZoho}
        isSyncing={isSyncing}
        isLoadingZoho={isLoadingZoho || isLoadingCustomers}
        onImportZoho={handleZohoImport}
        onInitialImportZoho={handleZohoInitialImport}
        initialImportUnavailable={zohoInitialImportUnavailable}
        onAdd={() => setIsAdding(true)}
      />

      <ZohoInitialImportModal
        show={showZohoInitialImportModal}
        isLoading={isLoadingZoho}
        summary={zohoInitialImportSummary}
        samples={zohoInitialImportSamples}
        onRefreshPreview={handleZohoInitialImport}
        onClose={() => {
          if (isLoadingZoho) return;
          setShowZohoInitialImportModal(false);
          setZohoInitialImportSummary(null);
          setZohoInitialImportSamples(null);
        }}
      />

      <ZohoImportModal
        show={showZohoModal}
        onClose={() => {
          setShowZohoModal(false);
          setZohoSearchQuery('');
          setZohoContacts([]);
          setZohoPreviewSummary(null);
          setLinkTargetCustomer(null);
          setZohoModalMode('import');
          setLinkingContactId(null);
        }}
        mode={zohoModalMode}
        linkTarget={linkTargetCustomer}
        zohoContacts={zohoContacts}
        isSearching={isSearchingZoho}
        searchQuery={zohoSearchQuery}
        setSearchQuery={setZohoSearchQuery}
        selectedCategoryId={selectedCategoryId}
        setSelectedCategoryId={setSelectedCategoryId}
        categories={categories}
        previewSummary={zohoPreviewSummary}
        onboardingContactId={zohoModalMode === 'link' ? linkingContactId : onboardingContactId}
        isLoadingZoho={isLoadingZoho || isSyncing}
        customers={customers}
        onOnboardContact={handleOnboardContact}
        onLinkContact={handleLinkContact}
        onSyncAll={handleZohoSync}
      />

      <CustomerAddForm
        isAdding={isAdding}
        categories={categories}
        formData={formData}
        setFormData={setFormData}
        editCurrency={editCurrency}
        onCancel={() => setIsAdding(false)}
        onSubmit={handleAddCustomer}
      />

      <CommercialTermsModal
        editingCustomer={editingCustomer}
        categories={categories}
        editCategoryId={editCategoryId}
        setEditCategoryId={setEditCategoryId}
        applyCategoryDefaults={applyCategoryDefaults}
        setApplyCategoryDefaults={setApplyCategoryDefaults}
        editTerms={editTerms}
        setEditTerms={setEditTerms}
        editCurrency={editCurrency}
        isSavingTerms={isSavingTerms}
        onCancel={() => setEditingCustomer(null)}
        onSave={saveTerms}
        editingQuotes={editingQuotes}
        zohoFinance={zohoFinance}
      />

      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Directory Window</p>
            <p className="mt-1 text-sm font-semibold text-slate-700">
              Showing {pageStart}-{pageEnd} of {totalCount.toLocaleString()} clients
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => void refreshCurrentPage()}
              disabled={isLoadingCustomers}
              className={`rounded-xl px-4 py-2 text-xs font-black uppercase tracking-widest ${
                isLoadingCustomers ? 'bg-slate-100 text-slate-400' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {isLoadingCustomers ? 'Refreshing...' : 'Refresh Page'}
            </button>
            <button
              onClick={() => void refreshCurrentPage(customerPage - 1)}
              disabled={isLoadingCustomers || customerPage <= 1}
              className={`rounded-xl px-4 py-2 text-xs font-black uppercase tracking-widest ${
                isLoadingCustomers || customerPage <= 1
                  ? 'bg-slate-100 text-slate-400'
                  : 'bg-slate-900 text-white hover:bg-black'
              }`}
            >
              Previous
            </button>
            <span className="text-xs font-bold text-slate-500">
              Page {customerPage} / {totalPages}
            </span>
            <button
              onClick={() => void refreshCurrentPage(customerPage + 1)}
              disabled={isLoadingCustomers || customerPage >= totalPages}
              className={`rounded-xl px-4 py-2 text-xs font-black uppercase tracking-widest ${
                isLoadingCustomers || customerPage >= totalPages
                  ? 'bg-slate-100 text-slate-400'
                  : 'bg-slate-900 text-white hover:bg-black'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {isLoadingCustomers && pagedCustomers.length === 0 ? (
        <div className="rounded-[2.5rem] border border-slate-100 bg-white px-8 py-16 text-center text-sm font-semibold text-slate-500 shadow-sm">
          Loading customer summaries...
        </div>
      ) : (
        <>
          {top10ByTransactions.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Top 10 by Transactions</h3>
                <span className="text-[10px] font-bold text-slate-400">Auto-prioritized from this page</span>
              </div>
              <div className="grid grid-cols-1 gap-8">{top10ByTransactions.map((customer) => renderCustomerCard(customer, true))}</div>
            </div>
          )}

          {regularCustomers.length > 0 ? (
            <div className="grid grid-cols-1 gap-8">{regularCustomers.map((customer) => renderCustomerCard(customer))}</div>
          ) : top10ByTransactions.length === 0 ? (
            <div className="rounded-[2.5rem] border border-slate-100 bg-white px-8 py-16 text-center text-sm font-semibold text-slate-500 shadow-sm">
              No customers found for the current filters.
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default CustomerManager;
